import { createI18n } from 'vue-i18n'
import { compile, registerMessageCompiler } from '@intlify/core-base'
import en from './locales/en'
import nl from './locales/nl'
import fr from './locales/fr'

// @intlify/unplugin-vue-i18n precompiles locale messages into AST resources at
// build time (see vite.config.ts), and the production build aliases 'vue-i18n'
// to its runtime-only build so no eval-based JIT compiler ships to the browser
// (required for CSP script-src without 'unsafe-eval'). The runtime-only build
// does not wire up any message compiler by default, even to just interpret an
// already-precompiled AST — it must be registered explicitly, or `t()` throws
// at the first call (vue-i18n's own internal "did this resolve to a string?"
// check fails and it raises an error). `compile` here is @intlify/core-base's
// AST interpreter: for a precompiled AST resource it returns a plain closure
// that walks the AST at call time — no `new Function()`/eval involved, so this
// keeps the CSP guarantee this whole precompilation setup exists for.
registerMessageCompiler(compile)

const STORAGE_KEY = 'startline_locale'
const SUPPORTED = ['en', 'nl', 'fr'] as const
type Locale = typeof SUPPORTED[number]

function detectLocale(): Locale {
  const stored = localStorage.getItem(STORAGE_KEY)
  if (stored && (SUPPORTED as readonly string[]).includes(stored)) {
    return stored as Locale
  }
  const browser = navigator.language.slice(0, 2)
  if ((SUPPORTED as readonly string[]).includes(browser)) {
    return browser as Locale
  }
  return 'en'
}

const i18n = createI18n({
  legacy: false,
  locale: detectLocale(),
  fallbackLocale: 'en',
  messages: { en, nl, fr },
})

export function setLocale(locale: Locale): void {
  (i18n.global.locale as { value: Locale }).value = locale
  localStorage.setItem(STORAGE_KEY, locale)
  document.documentElement.lang = locale
  document.cookie = `locale=${locale};path=/;max-age=31536000;SameSite=Lax`
}

/**
 * The active vue-i18n locale, narrowed to a supported `Locale`. Never reads
 * or writes localStorage or the cookie - use this whenever a supported-locale
 * value (rather than the raw i18n string) needs to leave the i18n module,
 * e.g. to send as `preferred_language` in a signup payload.
 */
export function currentLocale(): Locale {
  const active = (i18n.global.locale as { value: string }).value
  return (SUPPORTED as readonly string[]).includes(active) ? (active as Locale) : 'en'
}

export { SUPPORTED }
export type { Locale }
export default i18n
